import { useState } from 'react';
import { LuBan, LuSlidersHorizontal } from 'react-icons/lu';

import { RestrictionType } from '@ndvr/portfolio-api';

import { TickerLogo } from '@/components/TickerLogo';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useRequiredParams } from 'utils';
import { categoryIconMap } from 'views/portfolio/PortfolioPage/tabs/equity-preferences/constants';

import { PersonalizedIndexingRow } from './policy-personalized-indexing';
import { PolicyQueryError } from './PolicyQueryError';
import {
    PolicyTable,
    PolicyTableBody,
    PolicyTableCell,
    PolicyTableHead,
    PolicyTableHeader,
    PolicyTableRow,
} from './PolicyTable';
import { PositionAmount } from './PositionAmount';
import { usePolicyPersonalizedIndexing } from './usePolicyPersonalizedIndexing';

const securityBadgeClasses: Partial<Record<RestrictionType, string>> = {
    [RestrictionType.DO_NOT_BUY]: 'bg-[#f7e5e2] text-[#963125]',
    [RestrictionType.DO_NOT_SELL]: 'bg-[#f8ecdf] text-[#835c34]',
};

const categoryIcons = new Map(Object.entries(categoryIconMap));
const statusMessages = {
    loading: 'Loading personalized indexing restrictions…',
    ready: 'No customizations yet.',
};

const Restriction = ({ row }: { row: PersonalizedIndexingRow }) => {
    const CategoryIcon = row.kind === 'category' ? (categoryIcons.get(row.categoryId) ?? LuBan) : LuBan;
    return (
        <div className="flex items-center gap-3">
            <span className="text-cocoa-600 flex size-8 shrink-0 items-center justify-center rounded-md border border-stone-200 p-1">
                {row.kind === 'category' ? (
                    <CategoryIcon aria-hidden="true" className="size-4" />
                ) : (
                    <TickerLogo tickerSymbol={row.symbol} className="size-full object-contain" />
                )}
            </span>
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="font-medium">{row.label}</span>
                {row.kind === 'security' && row.inUniverse === false && (
                    <span className="text-cocoa-400 text-xs">Not in universe anymore</span>
                )}
            </span>
        </div>
    );
};

const RestrictionTypeBadge = ({ row }: { row: PersonalizedIndexingRow }) => (
    <span
        className={`inline-block rounded-[4px] px-2 py-1 text-xs leading-normal font-medium whitespace-nowrap ${
            row.kind === 'category'
                ? 'bg-[#f2e8f7] text-[#6c3f8a]'
                : (securityBadgeClasses[row.restrictionType] ?? 'bg-[#eeece9] text-[#5c5552]')
        }`}
    >
        {row.type}
    </span>
);

const CollapsedRestrictions = ({ rows }: { rows: PersonalizedIndexingRow[] }) => (
    <TooltipProvider>
        <span className="ml-auto flex flex-wrap items-center gap-2">
            {[
                { kind: 'category', label: 'SRI screens', chipClass: 'border-[#e5d5ec] bg-[#f2e8f7] text-[#6c3f8a]' },
                {
                    kind: 'security',
                    label: 'Client restrictions',
                    chipClass: 'border-stone-200 bg-stone-100 text-cocoa-900',
                },
            ].map(({ kind, label, chipClass }) => {
                const restrictions = rows.filter((row) => row.kind === kind);
                if (!restrictions.length) return null;
                return (
                    <span key={kind} className="inline-flex flex-wrap items-center gap-2">
                        {restrictions.slice(0, 3).map((row) => (
                            <Tooltip key={row.key}>
                                <TooltipTrigger asChild>
                                    <span
                                        aria-label={`${row.type}: ${row.label}`}
                                        className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium ${chipClass}`}
                                    >
                                        {row.label}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {row.kind === 'category' ? 'SRI screen' : `Client restriction · ${row.type}`}
                                    {row.kind === 'security' &&
                                        row.inUniverse === false &&
                                        ' · Not in universe anymore'}
                                </TooltipContent>
                            </Tooltip>
                        ))}
                        {restrictions.length > 3 && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="text-cocoa-600 text-xs">+{restrictions.length - 3} more</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {label}:{' '}
                                    {restrictions
                                        .slice(3)
                                        .map((row) => row.label)
                                        .join(', ')}
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </span>
                );
            })}
        </span>
    </TooltipProvider>
);

export const PolicyPersonalizedIndexing = () => {
    const [expanded, setExpanded] = useState(true);
    const { accountGUID, accountGroupGUID } = useRequiredParams(['accountGUID', 'accountGroupGUID']);
    const state = usePolicyPersonalizedIndexing(accountGUID, accountGroupGUID);
    const rows = state.status === 'ready' ? state.rows : [];
    const positionsStatus = state.status === 'ready' ? state.positionsStatus : 'loading';

    return (
        <Card className="gap-0 overflow-hidden rounded-[10px] border-stone-200 py-0 shadow-none">
            <h3 id="policy-personalized-indexing-title">
                <button
                    type="button"
                    className="flex w-full cursor-pointer flex-wrap items-center gap-3.5 px-6 py-[22px] text-left"
                    aria-expanded={expanded}
                    aria-controls="policy-personalized-indexing-details"
                    onClick={() => setExpanded(!expanded)}
                >
                    <LuSlidersHorizontal
                        aria-hidden="true"
                        className="size-8 shrink-0 rounded-md bg-stone-900 p-2 text-white"
                    />
                    <span className="text-cocoa-1000 flex-1 text-[15px] font-medium">
                        Personalized Indexing Customization
                    </span>
                    {!expanded && state.status === 'ready' && <CollapsedRestrictions rows={rows} />}
                    <span className="text-cocoa-600 shrink-0 text-xs">
                        {expanded ? 'Hide details' : 'Show details'}
                    </span>
                </button>
            </h3>
            {expanded && (
                <div id="policy-personalized-indexing-details">
                    {positionsStatus === 'error' && (
                        <PolicyQueryError
                            message="Couldn’t load position amounts. Saved restrictions are still available."
                            queries={state.retryQueries}
                        />
                    )}
                    {state.status === 'error' ? (
                        <PolicyQueryError message="Couldn’t load restrictions." queries={state.retryQueries} />
                    ) : (
                        <PolicyTable className="min-w-[760px]" containerClassName="rounded-none border-0 border-t">
                            <PolicyTableHeader>
                                <PolicyTableRow>
                                    {['Restriction', 'Type', 'Details', 'Position'].map((label) => (
                                        <PolicyTableHead
                                            key={label}
                                            className={label === 'Position' ? 'text-right' : undefined}
                                        >
                                            {label}
                                        </PolicyTableHead>
                                    ))}
                                </PolicyTableRow>
                            </PolicyTableHeader>
                            <PolicyTableBody>
                                {rows.length === 0 ? (
                                    <PolicyTableRow>
                                        <PolicyTableCell
                                            colSpan={4}
                                            className="text-cocoa-500 py-8 text-center"
                                            role="status"
                                        >
                                            {statusMessages[state.status]}
                                        </PolicyTableCell>
                                    </PolicyTableRow>
                                ) : (
                                    rows.map((row) => (
                                        <PolicyTableRow key={row.key}>
                                            <PolicyTableCell>
                                                <Restriction row={row} />
                                            </PolicyTableCell>
                                            <PolicyTableCell>
                                                <RestrictionTypeBadge row={row} />
                                            </PolicyTableCell>
                                            <PolicyTableCell className="text-cocoa-700 whitespace-nowrap">
                                                {row.details}
                                            </PolicyTableCell>
                                            <PolicyTableCell
                                                className={`text-right tabular-nums ${row.position === 0 ? 'text-cocoa-400' : ''}`}
                                            >
                                                <PositionAmount value={row.position} status={positionsStatus} />
                                            </PolicyTableCell>
                                        </PolicyTableRow>
                                    ))
                                )}
                            </PolicyTableBody>
                        </PolicyTable>
                    )}
                </div>
            )}
        </Card>
    );
};
